import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Alert, Button, Card, Col, Modal, Row, Select, Space, Statistic, Table, Tag, Typography, message,
} from "antd";
import { DeleteOutlined, EyeOutlined, ThunderboltOutlined, WarningFilled } from "@ant-design/icons";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import { rightsService } from "../../../services/rightsService";

const { Title, Text } = Typography;
const PRIMARY = "#242166";
const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

/**
 * Suppression (soft-delete) de TOUS les lots d'attestations d'une compagnie ainsi que de leurs
 * dispatches : on les désactive (active_data = false) → ils disparaissent de tous les écrans,
 * l'historique est conservé (réversible). Aperçu obligatoire avant exécution. HME001.
 */
const SuppressionLotsAttestations = () => {
    const [compagnies, setCompagnies] = useState([]);
    const [compagnie, setCompagnie] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [executing, setExecuting] = useState(false);
    const canExecute = rightsService("HME001");
    const [searchParams] = useSearchParams();

    useEffect(() => {
        axiosInstance.get("/api/organisme/assurance")
            .then((res) => setCompagnies(res.data || []))
            .catch(() => message.error("Erreur lors du chargement des compagnies."));
    }, []);

    // Pré-sélection + aperçu auto quand on arrive depuis la page des lots d'une compagnie (?compagnie=CODE).
    useEffect(() => {
        const c = searchParams.get("compagnie");
        if (c) { setCompagnie(c); simulerFor(c); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const simulerFor = (code) => {
        if (!code) { message.warning("Sélectionnez une compagnie."); return; }
        setLoading(true);
        setPreview(null);
        axiosInstance.get(`/api/lot-attestation/suppression/simuler/${code}`)
            .then((res) => {
                setPreview({ ...res.data, execute: false });
                if (!res.data?.nbLots) {
                    message.info("Aucun lot actif à supprimer pour cette compagnie.");
                }
            })
            .catch(() => message.error("Erreur lors de la simulation."))
            .finally(() => setLoading(false));
    };
    const simuler = () => simulerFor(compagnie);

    const executer = () => {
        Modal.confirm({
            title: "Confirmer la suppression de TOUS les lots",
            icon: <WarningFilled style={{ color: "#d4380d" }} />,
            content: (
                <div>
                    <p>Cette opération va <b>désactiver tous les lots</b> ({fmt(preview?.nbLots)}) et
                        leurs <b>dispatches</b> ({fmt(preview?.nbDispatches)}) de la compagnie
                        <b> {preview?.compagnieNom || compagnie}</b>.</p>
                    <p>Les lots disparaîtront de tous les écrans. L'opération est <b>réversible</b> (historique
                        conservé) et <b>historisée</b>.</p>
                    <p style={{ color: "#888" }}>Confirmer ?</p>
                </div>
            ),
            okText: "Supprimer tous les lots",
            okButtonProps: { danger: true },
            cancelText: "Annuler",
            onOk() {
                setExecuting(true);
                return axiosInstance.post(`/api/lot-attestation/suppression/executer/${compagnie}`)
                    .then((res) => { setPreview({ ...res.data, execute: true }); message.success("Lots supprimés avec succès."); })
                    .catch((e) => message.error(e?.response?.data?.message || "Erreur lors de la suppression."))
                    .finally(() => setExecuting(false));
            },
        });
    };

    const lotCols = [
        { title: "Type", dataIndex: "type", key: "t", render: (v) => v || "-" },
        { title: "Catégorie", dataIndex: "categorie", key: "c", render: (v) => v || "-" },
        { title: "Série", dataIndex: "serie", key: "s", render: (v) => v || "-" },
        {
            title: "Plage", key: "p",
            render: (_, r) => <Text code>{r.debut != null ? `${fmt(r.debut)} - ${fmt(r.fin)}` : (r.codeId || "-")}</Text>,
        },
        { title: "Nombre", dataIndex: "taille", key: "n", align: "right", render: (v) => <b>{fmt(v)}</b> },
        { title: "Dispatches", dataIndex: "nbDispatches", key: "d", align: "right", render: (v) => <Tag color="blue">{fmt(v)}</Tag> },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PRODUCTION"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <Title level={3} style={{ color: PRIMARY }}><DeleteOutlined /> Suppression des lots d'attestations</Title>
                            <Text type="secondary">
                                Désactive (soft-delete) tous les lots d'attestations d'une compagnie ainsi que leurs
                                dispatches. Réversible (historique conservé). Aperçu obligatoire avant exécution.
                            </Text>

                            <Card style={{ borderRadius: 12, marginTop: 16 }}>
                                <Space wrap>
                                    <Select
                                        showSearch optionFilterProp="label" style={{ width: 340 }}
                                        placeholder="Sélectionner la compagnie"
                                        value={compagnie} onChange={(v) => { setCompagnie(v); setPreview(null); }}
                                        options={compagnies.map((c) => ({ value: c.code, label: c.raisonSociale }))}
                                    />
                                    <Button type="primary" icon={<EyeOutlined />} loading={loading} onClick={simuler}
                                        style={{ background: PRIMARY }}>
                                        Simuler (aperçu)
                                    </Button>
                                    <Button danger icon={<ThunderboltOutlined />} loading={executing}
                                        disabled={!preview || preview.execute || !canExecute || !preview.nbLots}
                                        onClick={executer}>
                                        Supprimer tous les lots
                                    </Button>
                                </Space>
                                {!canExecute && (
                                    <Alert style={{ marginTop: 12 }} type="info" showIcon
                                        message="Exécution réservée à l'habilitation HME001 (l'aperçu reste disponible)." />
                                )}
                            </Card>

                            {preview && (
                                <>
                                    {preview.execute && (
                                        <Alert style={{ marginTop: 16 }} type="success" showIcon
                                            message="Suppression exécutée." description="Les lots et dispatches ci-dessous ont été désactivés." />
                                    )}
                                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                        <Col xs={12} md={8}><Card><Statistic title="Lots concernés" value={preview.nbLots} valueStyle={{ color: "#d4380d" }} groupSeparator=" " /></Card></Col>
                                        <Col xs={12} md={8}><Card><Statistic title="Dispatches concernés" value={preview.nbDispatches} groupSeparator=" " /></Card></Col>
                                        <Col xs={12} md={8}><Card><Statistic title="Numéros au total" value={preview.totalNumeros} groupSeparator=" " /></Card></Col>
                                    </Row>

                                    <Card title={`Lots ${preview.execute ? "supprimés" : "qui seront supprimés"} — ${preview.nbLots}`}
                                        style={{ borderRadius: 12, marginTop: 16 }}>
                                        <Table rowKey={(r, i) => r.codeId || `${r.debut}-${r.fin}-${i}`} size="small"
                                            columns={lotCols} dataSource={preview.lots}
                                            pagination={{ pageSize: 10, showSizeChanger: true }} />
                                    </Card>
                                </>
                            )}
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuppressionLotsAttestations;
